import { useCallback, useSyncExternalStore } from "react";
import {
  useDistributeFirmQuotaMutation,
  useDistributeFirmQuotasMutation,
} from "../../store/storageRoles/firmApi";
import useAuth from "../useAuth";

// Firma kota dağıtımı — panelin her yerinden (firma cihazlar sayfası, cihaz
// listesi aksiyonları) aynı firma için tek dağıtım. Dağıtım sürerken o firmanın
// kimliği modül düzeyindeki kayıtta durur; abone olan her bileşen firmayı
// meşgul gösterir. Yalnız bu sekmedeki istekleri bilir (başka kullanıcıyı değil).

let busyFirmIds = new Set();
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getSnapshot = () => busyFirmIds;

const setBusy = (firmIds, busy) => {
  const next = new Set(busyFirmIds);
  [].concat(firmIds).forEach((id) => (busy ? next.add(id) : next.delete(id)));
  busyFirmIds = next;
  emit();
};

export const isFirmQuotaBusy = (firmId) => Boolean(firmId) && busyFirmIds.has(String(firmId));

export default function useFirmQuotaDistribution() {
  const { auth } = useAuth();
  const [distributeFirmQuota] = useDistributeFirmQuotaMutation();
  const [distributeFirmQuotas] = useDistributeFirmQuotasMutation();
  const busy = useSyncExternalStore(subscribe, getSnapshot);

  const isBusy = useCallback((firmId) => Boolean(firmId) && busy.has(String(firmId)), [busy]);

  // firm: { _id, firmName }. Aynı firma zaten dağıtılıyorsa yeni istek atılmaz (false döner).
  // Hata çağırana fırlatılır; mesajı çağıran gösterir.
  const distribute = useCallback(
    async (firm) => {
      const id = firm?._id ? String(firm._id) : "";
      if (!id || !firm.firmName || isFirmQuotaBusy(id)) return false;
      setBusy(id, true);
      try {
        await distributeFirmQuota({ firmName: firm.firmName, token: auth?.accessToken }).unwrap();
        return true;
      } finally {
        setBusy(id, false);
      }
    },
    [distributeFirmQuota, auth?.accessToken]
  );

  // Kapsamlı dağıtım (#386): firm = kök (sayfadaki firma), firmIds = seçicideki
  // firmalar (boş = tüm kapsam), scopeFirmIds = sürerken meşgul görünecekler.
  // Kapsamdan biri zaten dağıtılıyorsa yeni istek atılmaz (null döner).
  // Sunucu her firmayı kendi kilidinde işler; cevap { distributed, busy, skipped }.
  const distributeScope = useCallback(
    async (firm, firmIds = [], scopeFirmIds = []) => {
      const id = firm?._id ? String(firm._id) : "";
      const ids = [...new Set([id, ...scopeFirmIds.map(String)])].filter(Boolean);
      if (!id || ids.some(isFirmQuotaBusy)) return null;
      setBusy(ids, true);
      try {
        const res = await distributeFirmQuotas({
          firmId: id,
          firmIds: firmIds.map(String),
          token: auth?.accessToken,
        }).unwrap();
        return res?.data || { distributed: [], busy: [], skipped: [] };
      } finally {
        setBusy(ids, false);
      }
    },
    [distributeFirmQuotas, auth?.accessToken]
  );

  return { isBusy, distribute, distributeScope };
}
